import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Product } from '../../types/@types/product';
import type { Category } from '../../types/@types/category';
import CategoryBar from '../CategoryBar';
import { formatCurrency } from '../../utils/format-currency';
import { Button } from '../ui';

interface ProductPickerProps {
  products: Product[];
  categories: Category[];
  activeCategory: string | null;
  onCategorySelect: (categoryId: string | null) => void;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onProductClick: (product: Product) => void;
  pendingProductId?: string | null;
  isAddingProduct?: boolean;
  onGoToOrder?: () => void;
  onScrollPast?: (isPast: boolean) => void;
}

const DENSE_BREAKPOINT = 360;

export function ProductPicker({
  products,
  categories,
  activeCategory,
  onCategorySelect,
  searchTerm,
  onSearchTermChange,
  onProductClick,
  pendingProductId,
  isAddingProduct,
  onGoToOrder,
  onScrollPast,
}: ProductPickerProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [scrolledPast, setScrolledPast] = useState(false);
  const [panelWidth, setPanelWidth] = useState(0);
  const [denseOverride, setDenseOverride] = useState<boolean | null>(null);

  const filteredProducts = useMemo(() => products, [products]);
  const shouldSuggestDense = panelWidth > 0 && panelWidth < DENSE_BREAKPOINT;
  const denseList = denseOverride ?? shouldSuggestDense;
  const effectiveColumns = Math.max(
    1,
    denseList ? 1 : Math.floor(panelWidth / 240) || 1,
  );
  const totalRows = filteredProducts.length > 0 ? Math.ceil(filteredProducts.length / effectiveColumns) : 0;
  const shouldVirtualize = filteredProducts.length > 12;

  useEffect(() => {
    if (!onScrollPast && !onGoToOrder) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let animationFrame: number | null = null;
    const handleScroll = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(() => {
        const threshold = scroller.clientHeight;
        const next = scroller.scrollTop > threshold;
        setScrolledPast(next);
        onScrollPast?.(next);
        animationFrame = null;
      });
    };

    handleScroll();
    scroller.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      scroller.removeEventListener('scroll', handleScroll);
    };
  }, [onScrollPast, onGoToOrder]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setPanelWidth(entry.contentRect.width);
    });

    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (panelWidth >= DENSE_BREAKPOINT && denseOverride !== null) {
      setDenseOverride(null);
    }
  }, [panelWidth, denseOverride]);

  const rowVirtualizer = useVirtualizer({
    count: shouldVirtualize ? totalRows : 0,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => (denseList ? 156 : 220),
    overscan: 8,
  });

  const allowDenseToggle = panelWidth > 0 && panelWidth < 420;

  const renderProductCard = (product: Product) => {
    const isPending = isAddingProduct && String(pendingProductId) === String(product.id);
    return (
      <article
        key={product.id}
        role="listitem"
        className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-divider bg-surface p-4 shadow-sm shadow-slate-900/5 transition-all hover:shadow-md focus-within:ring-2 focus-within:ring-[color:var(--primary)]/70"
        style={{ borderRadius: 'calc(var(--radius) * 0.9)' }}
      >
        {/* {product.image_path ? (
          <div className="overflow-hidden rounded-xl bg-surface-muted">
            <img
              src={product.image_path}
              alt={product.name}
              className="h-36 w-full object-cover"
              loading="lazy"
            />
          </div>
        ) : null} */}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 className="truncate text-[length:clamp(16px,2.2vw,18px)] font-semibold text-[color:var(--text)]">
            {product.name}
          </h3>
          <p className="text-[length:clamp(13px,1.8vw,14px)] text-[color:var(--text-muted)]">
            {typeof product.category === 'string'
              ? product.category
              : product.category?.name}
          </p>
          <p className="text-[length:clamp(16px,2.4vw,18px)] font-semibold text-[color:var(--surface-contrast)]">
            {formatCurrency(product.price)}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            onClick={() => onProductClick(product)}
            disabled={isPending}
            size="lg"
            className="w-full"
            aria-label={`Agregar ${product.name}`}
          >
            {isPending ? 'Agregando…' : 'Agregar'}
          </Button>
        </div>
      </article>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between px-4 pb-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
            Productos
          </p>
          <h2 className="text-[clamp(20px,2.6vw,24px)] font-semibold text-[color:var(--surface-contrast)]">
            Selecciona y agrega al pedido
          </h2>
        </div>
        {allowDenseToggle ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={denseList}
            onClick={() => setDenseOverride((prev) => (prev === null ? !denseList : !prev))}
            className="ml-3 min-h-[40px] px-4 text-xs font-semibold"
          >
            {denseList ? 'Ver como grid' : 'Ver lista densa'}
          </Button>
        ) : null}
      </div>
      <CategoryBar
        categories={categories}
        activeId={activeCategory}
        onSelect={onCategorySelect}
      />
      <div className="px-4 pb-2 pt-4">
        <label htmlFor="product-search" className="sr-only">
          Buscar producto por nombre
        </label>
        <input
          id="product-search"
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          placeholder="Buscar producto"
          className="w-full rounded-full border border-divider bg-surface px-4 py-3 text-sm text-[color:var(--text)] placeholder:text-slate-500 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary)]/70"
        />
      </div>
      <div className="relative flex-1">
        <div
          ref={scrollerRef}
          className="ProductsPanel relative flex-1 overflow-y-auto px-4"
          role="list"
          aria-label="Listado de productos"
          style={{
            maxHeight: 'calc(100dvh - var(--pos-products-offset, 18rem))',
            paddingBottom: 'calc(var(--pos-bottom-nav-height, 4.5rem) + var(--safe-area-bottom))',
          }}
        >
          {filteredProducts.length > 0 ? (
            shouldVirtualize ? (
              <div
                style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
                className="relative"
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const startIndex = virtualRow.index * effectiveColumns;
                  const rowItems = filteredProducts.slice(
                    startIndex,
                    startIndex + effectiveColumns,
                  );

                  if (rowItems.length === 0) {
                    return null;
                  }

                  return (
                    <div
                      key={virtualRow.key}
                      ref={rowVirtualizer.measureElement}
                      data-index={virtualRow.index}
                      className="absolute inset-x-0"
                      style={{ transform: `translateY(${virtualRow.start}px)` }}
                    >
                      <div
                        className="grid gap-3 sm:gap-4"
                        style={{
                          gridTemplateColumns: `repeat(${denseList ? 1 : Math.min(effectiveColumns, rowItems.length)}, minmax(220px, 1fr))`,
                        }}
                      >
                        {rowItems.map(renderProductCard)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                className="grid gap-3 sm:gap-4"
                style={{
                  gridTemplateColumns: `repeat(${denseList ? 1 : Math.max(1, effectiveColumns)}, minmax(220px, 1fr))`,
                }}
              >
                {filteredProducts.map(renderProductCard)}
              </div>
            )
          ) : (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-center text-[color:var(--text-muted)]">
              <p className="text-sm font-medium">No encontramos productos.</p>
              <button
                type="button"
                onClick={() => onSearchTermChange('')}
                className="rounded-full border border-divider px-4 py-2 text-sm font-semibold text-[color:var(--text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary)]/70"
              >
                Ver todos
              </button>
            </div>
          )}
        </div>
        {scrolledPast && onGoToOrder ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center">
            <button
              type="button"
              onClick={onGoToOrder}
              className="pointer-events-auto inline-flex min-h-[44px] items-center gap-2 rounded-full border border-divider bg-surface px-5 text-sm font-semibold text-[color:var(--text)] shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary)]/70"
            >
              Ir al pedido
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ProductPicker;
